import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

interface IBackLayerStackContext {
  registerDismissLayer: (
    onDismiss: (pendingAction?: () => void) => void,
  ) => () => void;
  tryDismissTop: (pendingAction?: () => void) => boolean;
}

const BackLayerStackContext = createContext<IBackLayerStackContext | undefined>(
  undefined,
);

export function BackLayerStackProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const stackRef = useRef<((pendingAction?: () => void) => void)[]>([]);

  const registerDismissLayer = useCallback(
    (onDismiss: (pendingAction?: () => void) => void) => {
      stackRef.current.push(onDismiss);
      return () => {
        const idx = stackRef.current.lastIndexOf(onDismiss);
        if (idx >= 0) {
          stackRef.current.splice(idx, 1);
        }
      };
    },
    [],
  );

  const tryDismissTop = useCallback((pendingAction?: () => void) => {
    if (stackRef.current.length === 0) {
      return false;
    }
    const close = stackRef.current[stackRef.current.length - 1];
    if (close) {
      close(pendingAction);
      return true;
    }
    return false;
  }, []);

  const value = useMemo(
    () => ({ registerDismissLayer, tryDismissTop }),
    [registerDismissLayer, tryDismissTop],
  );

  return (
    <BackLayerStackContext.Provider value={value}>
      {children}
    </BackLayerStackContext.Provider>
  );
}

export function useBackLayerStack(): IBackLayerStackContext {
  const ctx = useContext(BackLayerStackContext);
  if (!ctx) {
    throw new Error(
      'useBackLayerStack must be used within BackLayerStackProvider',
    );
  }
  return ctx;
}

export function useRegisterDismissLayer(
  active: boolean,
  onDismiss: (pendingAction?: () => void) => void,
) {
  const { registerDismissLayer } = useBackLayerStack();
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const runDismiss = useCallback((pendingAction?: () => void) => {
    onDismissRef.current(pendingAction);
  }, []);

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    return registerDismissLayer(runDismiss);
  }, [active, registerDismissLayer, runDismiss]);
}
